import React, { useEffect, useRef, useState } from 'react';
import { Home, ChevronDown, ChevronRight } from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import WSEDocumentViewer from './WSEDocumentViewer';
import COSHH from './COSHH';
import OnSiteRA from './OnSiteRA';
import PVSPageLayout from './PVSPageLayout';
import HSafetyPolicy from './HSafetyPolicy';
import PryolysisWorkFlow from './PryolysisWorkFlow';
import ShotblastingWorkFlow from './ShotblastingWorkFlow';
import SprayboothWorkFlow from './SprayboothWorkFlow';
import LiftingTransport from './LiftingTransport';
import VesselPrep from './VesselPrep';
import VesselAirTest from './VesselAirTest';

// Amalgamated RA + SSOW documents
import ForkLiftTruckDoc from './documents/ForkLiftTruckDoc';
import ManualHandlingDoc from './documents/ManualHandlingDoc';
import JetWashPressureWasherDoc from './documents/JetWashPressureWasherDoc';
import PillarDrillDoc from './documents/PillarDrillDoc';
import WeldingDoc from './documents/WeldingDoc';
import BenchGrinderDoc from './documents/BenchGrinderDoc';
import GuillotineDoc from './documents/GuillotineDoc';
import PortableHandToolsDoc from './documents/PortableHandToolsDoc';

// Risk Assessment only documents
import WorkplaceTransportDoc from './documents/WorkplaceTransportDoc';
import DrivingGoodsVehiclesDoc from './documents/DrivingGoodsVehiclesDoc';
import DriverThirdPartySitesDoc from './documents/DriverThirdPartySitesDoc';
import LoneWorkingDoc from './documents/LoneWorkingDoc';
import OvergroundTankBasesDoc from './documents/OvergroundTankBasesDoc';
import DeGassingFlareDoc from './documents/DeGassingFlareDoc';
// Safe System of Work only documents
import AccessingTrailersDoc from './documents/AccessingTrailersDoc';
import FireGeneralSafetyDoc from './documents/FireGeneralSafetyDoc';
import FLTChangingLPGCylindersDoc from './documents/FLTChangingLPGCylindersDoc';
import LaddersStepladdersDoc from './documents/LaddersStepladdersDoc';
import OvensDoc from './documents/OvensDoc';
import PortableGasSpaceHeatersDoc from './documents/PortableGasSpaceHeatersDoc';
import SlingsChainsLiftingDoc from './documents/SlingsChainsLiftingDoc';

// Policy documents
import ImpartialityConfidentialityDoc from './documents/ImpartialityConfidentialityDoc';
import VesselTaggingPolicyDoc from './documents/VesselTaggingPolicyDoc';
import SiteLayoutsDoc from './documents/SiteLayoutsDoc';

// Additional RAMS documents
import PortableCompressorUnitDoc from './documents/PortableCompressorUnitDoc';

// Interactive process maps
import VesselRefurbishmentProcessMap from './documents/VesselRefurbishmentProcessMap';

import { canAccess } from '../config/userPermissions';
import { DocumentArchiveProvider } from './DocumentArchiveButton';
import { isCurrentUserAdmin } from '../utils/currentUser';
import {
  archiveDocumentName,
  archiveElementAsPdf,
  waitForArchiveReady,
} from '../utils/documentArchive';

const DOC_REGISTRY = {
  wse: WSEDocumentViewer,
  hsafety: HSafetyPolicy,
  pryolysis: PryolysisWorkFlow,
  shotblasting: ShotblastingWorkFlow,
  spraybooth: SprayboothWorkFlow,
  liftingtransport: LiftingTransport,
  vesselprep: VesselPrep,
  vesselairtest: VesselAirTest,
  COSHH,
  OnSiteRA,
  // RA + SSOW amalgamated
  ForkLiftTruckDoc,
  ManualHandlingDoc,
  JetWashPressureWasherDoc,
  PillarDrillDoc,
  WeldingDoc,
  BenchGrinderDoc,
  GuillotineDoc,
  PortableHandToolsDoc,
  // RA only
  WorkplaceTransportDoc,
  DrivingGoodsVehiclesDoc,
  DriverThirdPartySitesDoc,
  LoneWorkingDoc,
  OvergroundTankBasesDoc,
  DeGassingFlareDoc,
  // SSOW only
  AccessingTrailersDoc,
  FireGeneralSafetyDoc,
  FLTChangingLPGCylindersDoc,
  LaddersStepladdersDoc,
  OvensDoc,
  PortableGasSpaceHeatersDoc,
  SlingsChainsLiftingDoc,
  // Policies
  ImpartialityConfidentialityDoc,
  VesselTaggingPolicyDoc,
  SiteLayoutsDoc,
  // Additional RAMS
  PortableCompressorUnitDoc,
  // Process maps
  VesselRefurbishmentProcessMap,
};

const GROUPS = [
  {
    id: 'policies',
    title: 'Policies',
    items: [
      { key: 'hsafety', label: 'Health & Safety Policy' },
      { key: 'COSHH', label: 'COSHH' },
      { key: 'FireGeneralSafetyDoc', label: 'General Fire Safety' },
    ],
  },
  {
    id: 'vessel',
    title: 'Vessel Inspection and Maintenance',
    items: [
      { key: 'vesselprep', label: 'Vessel Inspection and Preparation' },
      { key: 'vesselairtest', label: 'Vessel Air Test' },
      { key: 'wse', label: 'Written Scheme of Examination' },
      { key: 'ImpartialityConfidentialityDoc', label: 'Impartiality & Confidentiality Agreement' },
      { key: 'VesselTaggingPolicyDoc', label: 'Vessel Tagging & Digital Application Policy' },
      { key: 'VesselRefurbishmentProcessMap', label: 'Vessel Refurbishment Process Map (Interactive)' },
      { key: 'PortableCompressorUnitDoc', label: 'Transfer of Liquid LPG' },
      { key: 'DeGassingFlareDoc', label: 'De-Gassing and Flare Operations' },
    ],
  },
  {
    id: 'surface',
    title: 'Surface Treatments and Preparations',
    items: [
      { key: 'pryolysis', label: 'Pryolysis' },
      { key: 'shotblasting', label: 'Shotblasting' },
      { key: 'spraybooth', label: 'Spraybooth' },
      { key: 'OvensDoc', label: 'Drying Ovens' },
    ],
  },
  {
    id: 'tools',
    title: 'Hand and Power Tools',
    items: [
      { key: 'PortableHandToolsDoc', label: 'Portable Hand Tools' },
      { key: 'PillarDrillDoc', label: 'Pillar Drill' },
      { key: 'BenchGrinderDoc', label: 'Bench Grinder' },
      { key: 'GuillotineDoc', label: 'Metal Cutting Guillotine' },
      { key: 'WeldingDoc', label: 'Welding, Plasma Cutting & Oxy-Gas' },
      { key: 'JetWashPressureWasherDoc', label: 'Jet Wash / Pressure Washers' },
    ],
  },
  {
    id: 'transport',
    title: 'Transport and Handling',
    items: [
      { key: 'liftingtransport', label: 'Lifting Operations and Transport' },
      { key: 'ForkLiftTruckDoc', label: 'Fork Lift Truck' },
      { key: 'FLTChangingLPGCylindersDoc', label: 'Changing LPG Cylinders (Forklifts)' },
      { key: 'ManualHandlingDoc', label: 'Manual Handling' },
      { key: 'SlingsChainsLiftingDoc', label: 'Slings, Chains and Lifting Appliances' },
      { key: 'WorkplaceTransportDoc', label: 'Workplace Transport' },
      { key: 'DrivingGoodsVehiclesDoc', label: 'Driving Goods Vehicles' },
      { key: 'DriverThirdPartySitesDoc', label: 'Driver Attending Third Party Sites' },
      { key: 'AccessingTrailersDoc', label: 'Accessing Trailers' },
    ],
  },
  {
    id: 'site',
    title: 'General Site Safety',
    items: [
      { key: 'OnSiteRA', label: 'On Site' },
      { key: 'SiteLayoutsDoc', label: 'Site Layouts' },
      { key: 'LoneWorkingDoc', label: 'Lone Working – Pyrolysis / Shot Blasting' },
      { key: 'LaddersStepladdersDoc', label: 'Ladders and Stepladders' },
      { key: 'PortableGasSpaceHeatersDoc', label: 'Portable Gas Space Heaters' },
      { key: 'OvergroundTankBasesDoc', label: 'Production of Bases for Overground Tanks' },
    ],
  },
];

const Documents = ({
  onReturnHome,
  onNavigateToForm,
  initialSelectedKey,
  onInitialSelectedKeyConsumed,
}) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [previousKey, setPreviousKey] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [isAdmin] = useState(() => isCurrentUserAdmin());
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const archiveSourceRef = useRef(null);

  // When the parent (PVSHomePage) asks us to open a specific doc on mount —
  // e.g. returning from a form that was opened from inside the Process Map —
  // honour that, then signal back so the prop can be cleared.
  useEffect(() => {
    if (initialSelectedKey && canAccess(initialSelectedKey) && DOC_REGISTRY[initialSelectedKey]) {
      setSelectedKey(initialSelectedKey);
      setPreviousKey(null);
    }
    if (initialSelectedKey && typeof onInitialSelectedKeyConsumed === 'function') {
      onInitialSelectedKeyConsumed();
    }
  }, [initialSelectedKey, onInitialSelectedKeyConsumed]);

  // Whenever the user opens, switches, or closes a document, jump to the top
  // of the page. Without this, switching from a long doc/process map leaves
  // the new view scrolled half-way down.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [selectedKey]);

  const handleReturnHome = () => {
    setSelectedKey(null);
    setPreviousKey(null);
  };

  const handleReturnToPrevious = () => {
    setSelectedKey(previousKey);
    setPreviousKey(null);
  };

  // Saves the document currently on screen as a PDF. Buttons, forms and the
  // acknowledgment block are stripped out so the archive reads as a static
  // record of the controlled document.
  const archiveCurrentDocument = async (label) => {
    if (!isAdmin || isArchiving) return;
    setIsArchiving(true);
    setArchiveError('');
    try {
      const root = await waitForArchiveReady(archiveSourceRef);
      await archiveElementAsPdf(root, archiveDocumentName(label));
    } catch (error) {
      console.error('Document archive failed:', error);
      setArchiveError('Failed to prepare the PDF. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  // Allow inner documents (e.g. the interactive process map) to navigate to a
  // related doc or form. Forms live on the PVSForms screen, so we delegate
  // to the parent (PVSHomePage) via the onNavigateToForm callback. We pass
  // the current selectedKey as the "source" so PVSHomePage can render a
  // back-to-process-map button on the form.
  const handleNavigateToDoc = (key, target) => {
    if (!canAccess(key)) return;
    if (target === 'form') {
      if (typeof onNavigateToForm === 'function') {
        onNavigateToForm(key, selectedKey);
      }
      return;
    }
    if (DOC_REGISTRY[key]) {
      setPreviousKey(selectedKey);
      setSelectedKey(key);
    }
  };

  if (selectedKey && DOC_REGISTRY[selectedKey] && canAccess(selectedKey)) {
    const Component = DOC_REGISTRY[selectedKey];
    const currentItem = GROUPS.flatMap((group) => group.items).find((item) => item.key === selectedKey);
    const label = currentItem?.label || selectedKey;
    return (
      <>
        <DocumentArchiveProvider
          value={{
            canArchive: isAdmin,
            isArchiving,
            onArchive: () => archiveCurrentDocument(label),
          }}
        >
          <div ref={archiveSourceRef}>
            <Component
              onReturnHome={handleReturnHome}
              onNavigateToDoc={handleNavigateToDoc}
            />
          </div>
        </DocumentArchiveProvider>
        {archiveError && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-lg border border-red-500/40 bg-red-900/80 text-red-100">
            <p className="text-sm font-medium">{archiveError}</p>
          </div>
        )}
        {previousKey && previousKey === 'VesselRefurbishmentProcessMap' && (
          <FloatingBackButton onClick={handleReturnToPrevious} label="Back to Process Map" />
        )}
      </>
    );
  }

  const visibleGroups = GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canAccess(item.key)),
    }))
    .filter((group) => group.items.length > 0);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="pvs-glass w-full">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">Documents</h1>
              <button
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover"
                onClick={onReturnHome}
                title="Return to Homepage"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {visibleGroups.length === 0 && (
              <p className="text-pvs-text-muted text-center py-4">
                No documents available for your account.
              </p>
            )}
            {visibleGroups.map((group) => {
              const isOpen = openGroups.has(group.id);
              return (
                <div key={group.id} className="pvs-glass rounded-xl overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-6 py-4 pvs-glass-hover"
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className="text-lg font-semibold text-white">{group.title}</span>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-pvs-accent" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-pvs-accent" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="grid gap-3 p-4 border-t border-pvs-border">
                      {group.items.map((item) => (
                        <button
                          key={item.key}
                          className="w-full h-14 pvs-glass pvs-glass-hover flex items-center justify-start px-5 rounded-lg"
                          onClick={() => setSelectedKey(item.key)}
                        >
                          <span className="text-base font-semibold text-white text-left">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PVSPageLayout>
  );
};

export default Documents;
